import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const ROLE_COLORS = { admin: 'error', manager: 'primary', bd: 'success', mr: 'warning', scientist: 'info' };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const NOTIF_EVENTS = [
  { key: 'order_update',     label: 'Order Update' },
  { key: 'inquiry_assigned', label: 'Inquiry Assigned' },
  { key: 'stock_alert',      label: 'Stock Alert' },
  { key: 'license_expiry',   label: 'License Expiry' },
  { key: 'leave_approved',   label: 'Leave Approved' },
  { key: 'payment_received', label: 'Payment Received' },
];
const NOTIF_CHANNELS = [
  { key: 'in_app',   label: 'In-App' },
  { key: 'email',    label: 'Email' },
  { key: 'whatsapp', label: 'WhatsApp' },
];

export default function Profile() {
  const { user } = useAuth();

  const [profileForm, setProfileForm] = useState({ name: '', phone: '', territory: '', department: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });

  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: '', text: '' });

  const [reporting, setReporting] = useState([]);
  const [reportingLoading, setReportingLoading] = useState(true);

  // Notification preferences state
  const [notifPrefs, setNotifPrefs]       = useState({});
  const [notifLoading, setNotifLoading]   = useState(true);
  const [notifSaving, setNotifSaving]     = useState(false);
  const [notifMsg, setNotifMsg]           = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      setProfileForm({
        name:       user.name       || '',
        phone:      user.phone      || '',
        territory:  user.territory  || '',
        department: user.department || '',
      });
    }
  }, [user]);

  useEffect(() => {
    api.get('/auth/reporting')
      .then(r => setReporting(r.data.data || []))
      .catch(() => {})
      .finally(() => setReportingLoading(false));
  }, []);

  // Load notification preferences
  useEffect(() => {
    setNotifLoading(true);
    api.get('/auth/notification-preferences')
      .then(r => {
        const data = r.data.data || r.data || {};
        // Normalise: build a prefs map { event_key: { in_app: bool, email: bool, whatsapp: bool } }
        const prefs = {};
        NOTIF_EVENTS.forEach(ev => {
          prefs[ev.key] = NOTIF_CHANNELS.reduce((acc, ch) => {
            acc[ch.key] = data[ev.key]?.[ch.key] ?? true;
            return acc;
          }, {});
        });
        setNotifPrefs(prefs);
      })
      .catch(() => {
        // Default all to true
        const prefs = {};
        NOTIF_EVENTS.forEach(ev => {
          prefs[ev.key] = NOTIF_CHANNELS.reduce((acc, ch) => { acc[ch.key] = true; return acc; }, {});
        });
        setNotifPrefs(prefs);
      })
      .finally(() => setNotifLoading(false));
  }, []);

  const handleNotifSave = async () => {
    setNotifSaving(true); setNotifMsg({ type: '', text: '' });
    try {
      await api.put('/auth/notification-preferences', notifPrefs);
      setNotifMsg({ type: 'success', text: 'Notification preferences saved.' });
    } catch (err) {
      setNotifMsg({ type: 'error', text: err?.response?.data?.message || 'Failed to save preferences.' });
    } finally { setNotifSaving(false); }
  };

  const toggleNotif = (eventKey, channelKey) => {
    setNotifPrefs(p => ({
      ...p,
      [eventKey]: { ...p[eventKey], [channelKey]: !p[eventKey]?.[channelKey] },
    }));
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!profileForm.name.trim()) { setProfileMsg({ type: 'error', text: 'Name is required.' }); return; }
    setProfileSaving(true); setProfileMsg({ type: '', text: '' });
    try {
      await api.put('/auth/profile', profileForm);
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err?.response?.data?.message || 'Failed to update profile.' });
    } finally { setProfileSaving(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwMsg({ type: 'error', text: 'New passwords do not match.' }); return;
    }
    if (pwForm.new_password.length < 6) {
      setPwMsg({ type: 'error', text: 'New password must be at least 6 characters.' }); return;
    }
    setPwSaving(true); setPwMsg({ type: '', text: '' });
    try {
      await api.put('/auth/change-password', {
        current_password: pwForm.current_password,
        new_password:     pwForm.new_password,
      });
      setPwMsg({ type: 'success', text: 'Password changed successfully.' });
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err?.response?.data?.message || 'Failed to change password.' });
    } finally { setPwSaving(false); }
  };

  if (!user) return null;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Profile</h1>
          <p className="page-subtitle">Manage your account details and security settings</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Account info (read-only) */}
          <div className="card">
            <div className="card-header"><span className="card-title">Account Information</span></div>
            <div className="card-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%', background: 'var(--primary-lighter)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.6rem', fontWeight: 700, color: 'var(--primary)', flexShrink: 0,
                }}>
                  {user.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{user.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 4 }}>{user.email}</div>
                  <span className={`badge badge-${ROLE_COLORS[user.role] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>{user.role}</span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: '0.875rem' }}>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: 2 }}>Department</div>
                  <div style={{ fontWeight: 500 }}>{user.department || '—'}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: 2 }}>Territory</div>
                  <div style={{ fontWeight: 500 }}>{user.territory || '—'}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: 2 }}>Member Since</div>
                  <div style={{ fontWeight: 500 }}>{fmtDate(user.created_at)}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: 2 }}>Last Login</div>
                  <div style={{ fontWeight: 500 }}>{fmtDate(user.last_login)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Edit profile */}
          <div className="card">
            <div className="card-header"><span className="card-title">Edit Profile</span></div>
            <div className="card-body">
              {profileMsg.text && (
                <div className={`alert alert-${profileMsg.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: 14 }}>
                  {profileMsg.text}
                </div>
              )}
              <form onSubmit={handleProfileSave}>
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input className="form-control" value={profileForm.name}
                    onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-control" value={profileForm.phone}
                      onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Department</label>
                    <input className="form-control" value={profileForm.department}
                      onChange={e => setProfileForm(p => ({ ...p, department: e.target.value }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Territory / Region</label>
                  <input className="form-control" value={profileForm.territory}
                    onChange={e => setProfileForm(p => ({ ...p, territory: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="submit" className="btn btn-primary" disabled={profileSaving}>
                    {profileSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Change password */}
          <div className="card">
            <div className="card-header"><span className="card-title">Change Password</span></div>
            <div className="card-body">
              {pwMsg.text && (
                <div className={`alert alert-${pwMsg.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: 14 }}>
                  {pwMsg.text}
                </div>
              )}
              <form onSubmit={handlePasswordChange}>
                <div className="form-group">
                  <label className="form-label">Current Password *</label>
                  <input className="form-control" type="password" value={pwForm.current_password}
                    onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">New Password *</label>
                  <input className="form-control" type="password" value={pwForm.new_password}
                    onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm New Password *</label>
                  <input className="form-control" type="password" value={pwForm.confirm_password}
                    onChange={e => setPwForm(p => ({ ...p, confirm_password: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="submit" className="btn btn-primary" disabled={pwSaving}>
                    {pwSaving ? 'Updating…' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Reporting employees */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">My Team</span>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{reporting.length} reporting</span>
            </div>
            {reportingLoading ? (
              <div style={{ padding: 20, textAlign: 'center' }}><div className="spinner" /></div>
            ) : reporting.length === 0 ? (
              <div style={{ padding: '20px 16px', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                No team members report to you.
              </div>
            ) : (
              <div>
                {reporting.map(emp => (
                  <div key={emp.id} style={{
                    padding: '12px 16px', borderBottom: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', background: 'var(--surface-2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-secondary)', flexShrink: 0,
                    }}>
                      {emp.name?.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{emp.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {emp.department || '—'}{emp.territory ? ` · ${emp.territory}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <span className={`badge badge-${ROLE_COLORS[emp.role] || 'neutral'}`} style={{ textTransform: 'capitalize', fontSize: '0.7rem' }}>{emp.role}</span>
                      {!emp.is_active && <span className="badge badge-neutral" style={{ fontSize: '0.65rem' }}>Inactive</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

      </div>

      {/* ── Notification Preferences ──────────────────────────── */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-header">
          <span className="card-title">Notification Preferences</span>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Choose how you receive alerts</span>
        </div>
        <div className="card-body">
          {notifMsg.text && (
            <div className={`alert alert-${notifMsg.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: 14 }}>
              {notifMsg.text}
            </div>
          )}
          {notifLoading ? (
            <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner" /></div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.75rem', borderBottom: '2px solid var(--border)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Event
                    </th>
                    {NOTIF_CHANNELS.map(ch => (
                      <th key={ch.key} style={{ textAlign: 'center', padding: '8px 16px', fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.75rem', borderBottom: '2px solid var(--border)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                        {ch.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {NOTIF_EVENTS.map((ev, idx) => (
                    <tr key={ev.key} style={{ background: idx % 2 === 0 ? 'transparent' : 'var(--surface-2)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--text-primary)' }}>
                        {ev.label}
                      </td>
                      {NOTIF_CHANNELS.map(ch => {
                        const checked = notifPrefs[ev.key]?.[ch.key] ?? true;
                        return (
                          <td key={ch.key} style={{ textAlign: 'center', padding: '10px 16px' }}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', position: 'relative' }}>
                              <input type="checkbox" checked={checked} onChange={() => toggleNotif(ev.key, ch.key)}
                                style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
                              <span style={{
                                display: 'inline-block', width: 36, height: 20, borderRadius: 10,
                                background: checked ? 'var(--primary)' : '#d1d5db',
                                transition: 'background 0.2s', position: 'relative', flexShrink: 0,
                              }}>
                                <span style={{
                                  position: 'absolute', top: 3, left: checked ? 18 : 3,
                                  width: 14, height: 14, borderRadius: '50%', background: '#fff',
                                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                }} />
                              </span>
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-primary" onClick={handleNotifSave} disabled={notifSaving || notifLoading}>
              {notifSaving ? 'Saving…' : 'Save Preferences'}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}
